import styled from 'styled-components';

import { StyledDefaultHeaderCell } from '../../../../cdl/TableBuilder/components/StyledDefaultHeaderCell';
import { StyledTable } from '../../../../cdl/TableBuilder/components/StyledTable';
import { GenericError } from '../../../../common/Error/GenericError';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';
import type { OrderModel } from '../../../model/OrderModel';
import { useOrderOffers } from '../../hooks/useOrderOffers';

import { buildLineItemComparisonMatrix } from './buildLineItemComparisonMatrix';
import { LineItemComparisonRow } from './components/LineItemComparisonRow';
import { LineItemComparisonTotalRow } from './components/LineItemComparisonTotalRow';
import { SupplierHeader } from './components/SupplierHeader';
import {
    LINE_ITEM_COMPARISON_MIN_WIDTH,
    LINE_ITEM_COMPARISON_PRODUCT_COLUMN_MAX_WIDTH,
    LINE_ITEM_COMPARISON_SUPPLIER_COLUMN_MIN_WIDTH,
} from './lineItemComparisonDimensions';
import { stickyProductColumnStyles } from './stickyProductColumnStyles';
import { useHorizontalScrollShadows } from './useHorizontalScrollShadows';

const MatrixTable = styled(StyledTable)<{ $supplierCount: number }>`
    min-width: ${({ $supplierCount }) =>
        Math.max(
            LINE_ITEM_COMPARISON_MIN_WIDTH,
            LINE_ITEM_COMPARISON_PRODUCT_COLUMN_MAX_WIDTH +
                $supplierCount * LINE_ITEM_COMPARISON_SUPPLIER_COLUMN_MIN_WIDTH
        )}px;
    border: 0;
    table-layout: auto;
`;

const ProductColumn = styled.col`
    width: 1%;
`;

const SupplierColumn = styled.col`
    width: ${LINE_ITEM_COMPARISON_SUPPLIER_COLUMN_MIN_WIDTH}px;
`;

const ProductHeaderCell = styled(StyledDefaultHeaderCell)<{ $showScrollShadow: boolean }>`
    ${stickyProductColumnStyles};
    z-index: 2;
    background-color: ${theme.colors.background.default};
`;

const RightScrollShadow = styled.div<{ $visible: boolean }>`
    position: absolute;
    inset-block: 0;
    right: 0;
    width: ${theme.space[8]};
    box-shadow: inset -8px 0 8px -8px rgba(25, 54, 67, 0.15);
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 75ms ease-in-out;
    pointer-events: none;
    z-index: 3;
`;

interface LineItemComparisonMatrixProps {
    order: OrderModel;
}

export const LineItemComparisonMatrix = ({ order }: LineItemComparisonMatrixProps) => {
    const offersQuery = useOrderOffers(order);
    const matrix = buildLineItemComparisonMatrix(order, offersQuery.data ?? []);
    const { scrollRef, showLeftShadow, showRightShadow, handleScroll } = useHorizontalScrollShadows();

    if (offersQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (offersQuery.isError) {
        return <GenericError />;
    }

    return (
        <Box display="grid" rowGap={8}>
            <Box position="relative" minWidth={0}>
                <Box
                    ref={scrollRef}
                    onScroll={handleScroll}
                    width="100%"
                    minWidth={0}
                    overflowX="auto"
                    border="1px solid"
                    borderColor="background.inset"
                >
                    <MatrixTable variant="default" $supplierCount={matrix.columns.length}>
                        <colgroup>
                            <ProductColumn />
                            {matrix.columns.map((column) => (
                                <SupplierColumn key={column.offerId} />
                            ))}
                        </colgroup>
                        <thead>
                            <tr>
                                <ProductHeaderCell $showScrollShadow={showLeftShadow} />
                                {matrix.columns.map((column) => (
                                    <SupplierHeader key={column.offerId} column={column} order={order} />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {matrix.rows.map((row) => (
                                <LineItemComparisonRow key={row.id} row={row} showScrollShadow={showLeftShadow} />
                            ))}
                            <LineItemComparisonTotalRow totals={matrix.totals} showScrollShadow={showLeftShadow} />
                        </tbody>
                    </MatrixTable>
                </Box>
                <RightScrollShadow $visible={showRightShadow} />
            </Box>
        </Box>
    );
};
